import { useAutoAnimate } from '@formkit/auto-animate/react';
import { Add, Delete, RecentActors, SaveAlt, SupervisedUserCircle, UploadFile, Visibility, VisibilityOff } from '@mui/icons-material';
import { SvgIcon } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Icon } from '@mdi/react';
import { mdiDownload, mdiDownloadBoxOutline, mdiExport, mdiExportVariant, mdiFileDownload, mdiFileImport, mdiImport, mdiUpload } from '@mdi/js';
import { MiscTasks, Publisher } from '../../api/relevant';
import { ReactComponent as FileTree } from '../../assets/icons/FileTree.svg?component';
import AccessHistory from '../../components/AccessHistory';
import BulkProgrammaticImport from '../../components/BulkProgrammaticImport';
import { Button } from '../../components/Button/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import DemoImport from '../../components/DemoImport';
import PopupSelector from '../../components/PopupSelector';
import { SimpleTable } from '../../components/SimpleTable';
import { Tooltip } from '../../components/Tooltip';
import { Toast } from '../../components/Toast/Toast';
import { IfDemoMode, IfSuperAdmin } from '../../components/Wrappers';
import { useOperationToast } from '../../hooks/useOperationToast';
import { useToast } from '../../hooks/useToast';
import useTopBar from '../../hooks/useTopbar';
import { CsvImportManager, exportPublisherStructureV2 } from './CsvImportManager/CsvImportManager';
import { FuzzyFilterBar, FuzzyFilterResults } from './FuzzyFilterBar';
import { exportPublisherStructureV1, exportPrebidServerIds } from './utils';
import { usePublishersQuery } from './query';
import { SmartHelp } from '../../components/Smarts/SmartHelp';
import { TopBar } from '../../components/TopBar/TopBar';
import { IconButton } from '../../components/Button/IconButton';
import { MoreMenu, MenuItem } from '../../components/MoreMenu/MoreMenu';
import { PageTitle } from '../../components/PageTitle/PageTitle';
import { useDebounce } from '@uidotdev/usehooks';
import { debounce } from 'lodash';

export function AdminPublisherList() {
	const [csvUploadVisible, setCsvUploadVisible] = useState(false);
	const [showHidden, setShowHidden] = useState(false);
	const [csvUploadAnimate] = useAutoAnimate();
	const [, showToast] = useToast();
	const location = useLocation();
	const [filter, setFilter] = useState(new URLSearchParams(location.search).get('f') || '');

	let demoImport: DemoImport;

	const { isLoading, data: publishers } = usePublishersQuery();
	const queryClient = useQueryClient();

	const deleteMutation = useMutation({
		mutationFn: async ({ id }: { id: string }) => {
			await MiscTasks.call('deletePublisher', { id });
		},
		onSettled: async () => {
			await queryClient.invalidateQueries({ queryKey: ['publishers'] });
		},
		onSuccess: () => {
			showToast({
				message: 'Publisher deleted',
				status: 'success',
				timeout: 5,
			});
		},
		onError: () => {
			showToast({
				message: 'Failed to delete publisher',
				status: 'error',
			});
		},
	});

	const hideMutation = useMutation<void, Error, { id: string, hidden: boolean }>({
		mutationFn: async ({ id, hidden }: { id: string, hidden: boolean }) => {
			await Publisher.call('hide', { id, hidden });
		},
		onSettled: async () => {
			await queryClient.invalidateQueries({ queryKey: ['publishers'] });
		},

		onSuccess: (_, variables) => {
			showToast({
				message: variables.hidden ? 'Publisher hidden' : 'Publisher restored',
				status: 'success',
				timeout: 5,
			});
		},
		onError: (_, variables) => {
			showToast({
				message: variables?.hidden
					? 'Failed to hide publisher'
					: 'Failed to restore publisher',
				status: 'error',
			});
		},
	});

	const [rows, setRows] = useState<{ id: string; cols:(string | JSX.Element | undefined)[]; to: string; sort: string; hidden?: boolean }[]>([]);
	const [filterData, setFilterData] = useState<{ id: string; text: string }[]>();
	const [filterIds, setFilterIds] = useState<string[]>();

	useEffect(() => {
		const data = publishers?.map((p) => ({
			id: p.id,
			text: p.name,
		}));
		setFilterData(data);
	}, [publishers]);

	useEffect(() => {
		let r = publishers?.map((p) => ({
			id: p.id,
			cols: [
				p.hidden ? <span className="line-through">{p.name}</span> : p.name,
				// TODO: Implement "main contact"?
				p.programmaticInvoiceEmail,
				p.notes?.length ? p.notes : undefined,
			],
			to: `/accounts/${p.id}`,
			sort: p.name,
			hidden: p.hidden,
		})) ?? [];
		// Filter
		r = r.filter((row) => (!row.hidden || showHidden) && (!filterIds || filterIds.includes(row.id)));
		if (filterIds) {
			// Sort by filter score (awfully inefficient, but it's a small list)
			r = r.sort((a, b) => filterIds.indexOf(a.id) - filterIds.indexOf(b.id));
		} else {
			// Sort by name
			r = r.sort((a, b) => a.sort.localeCompare(b.sort));
		}
		// Optimistic delete
		if (deleteMutation.isPending && deleteMutation.variables) {
			const { id } = deleteMutation.variables;
			r = r.filter((row) => row.id !== id);
		}
		setRows(r);
	}, [publishers, showHidden, deleteMutation.isPending, filterIds]);

	// Debounced URL update to prevent browser throttling
	const updateUrl = useMemo(
		() => debounce((filterValue: string) => {
			const search = new URLSearchParams(location.search);
			if (filterValue) {
				search.set('f', filterValue);
			} else {
				search.delete('f');
			}
			window.history.replaceState(null, '', `${location.pathname}${search.size ? '?' : ''}${search}`);
		}, 100),
		[location],
	);

	const handleFilter = useCallback(({ results, value }: { results: FuzzyFilterResults, value: string }) => {
		setFilter(value);
		setFilterIds(results?.map((r) => r.item.id));
		updateUrl(value);
	}, [updateUrl]);

	const [csvDownloadOperation, csvDownloadInProgress] = useOperationToast({
		loadingMessage: 'Generating CSV...',
		errorMessage: (e) => `Failed to generate CSV. Please try again. (${e.message})`,
	});
	const handleCsvDownload = csvDownloadOperation(exportPublisherStructureV2);

	const [csvDownloadDuplicatesOperation, csvDownloadDuplicatesInProgress] = useOperationToast({
		loadingMessage: 'Generating CSV...',
		errorMessage: (e) => `Failed to generate CSV. Please try again. (${e.message})`,
	});
	const handleCsvDownloadDuplicates = csvDownloadDuplicatesOperation(exportPublisherStructureV1);

	const [csvDownloadPrebidServerIdOperation, csvDownloadPrebidServerIdInProgress] = useOperationToast({
		loadingMessage: 'Generating CSV...',
		errorMessage: (e) => `Failed to generate CSV. Please try again. (${e.message})`,
	});
	const handleCsvDownloadPrebidServerId = csvDownloadPrebidServerIdOperation(exportPrebidServerIds);

	return (
		<>
			<TopBar
				title="Publisher Accounts"
			/>

			{/* Top Bar */}
			<div className="container mx-auto">
				<PageTitle>Publisher Accounts</PageTitle>

				<div className="flex items-center justify-between flex-wrap gap-x-8 gap-y-2">
					<div className="flex items-center gap-2">
						<FuzzyFilterBar
							placeholder="Search publishers"
							data={filterData}
							value={filter}
							onFilter={handleFilter}
							shortcut="$mod+F"
						/>
						<IconButton icon={[<VisibilityOff />, <Visibility />]} tooltip={['Show hidden publishers', 'Hide hidden publishers']} active={showHidden} onToggle={setShowHidden} />
					</div>
					<div className="flex flex-wrap justify-end grow gap-x-8 gap-y-2">
						<div className="flex gap-2">
							<IfSuperAdmin>
								<PopupSelector
									size="lg"
									customLink={(showFn) => (
										<Tooltip title="Advanced Mapping">
											<IconButton
												icon={<SvgIcon ><FileTree /></SvgIcon>}
												onClick={showFn}
											/>
										</Tooltip>
									)}
								>
									<BulkProgrammaticImport />
								</PopupSelector>
							</IfSuperAdmin>
							<PopupSelector
								size="lg"
								customLink={(openFn) => (
									<Tooltip title="Access History">
										<IconButton icon={<RecentActors />} onClick={openFn} />
									</Tooltip>
								)}
							>
								<AccessHistory />
							</PopupSelector>
							<Tooltip title="Export...">
								<MoreMenu icon={<Icon path={mdiUpload} />}>
									<MenuItem icon={<Icon path={mdiUpload} />} onClick={handleCsvDownload} disabled={csvDownloadInProgress}>Export as CSV</MenuItem>
									<MenuItem icon={<Icon path={mdiUpload} />} onClick={handleCsvDownloadDuplicates} disabled={csvDownloadDuplicatesInProgress}>Export duplicates as CSV</MenuItem>
									<MenuItem icon={<Icon path={mdiUpload} />} onClick={handleCsvDownloadPrebidServerId} disabled={csvDownloadPrebidServerIdInProgress}>Export Prebid Server Account IDs as CSV</MenuItem>
								</MoreMenu>
							</Tooltip>
							<IconButton icon={<Icon path={mdiDownload} />} tooltip="Import..." active={csvUploadVisible} onClick={() => setCsvUploadVisible(!csvUploadVisible)} />
						</div>
						<Button href="/accounts/add" className="shrink-0">
							<Add /> New publisher
						</Button>
					</div>
				</div>
			</div>

			{/* CSV Upload */}
			<div ref={csvUploadAnimate}>
				{csvUploadVisible && (
					<div className="container mx-auto mt-4">
						<div className="border border-grey-100 rounded-md bg-white p-4">
							<CsvImportManager
								onImportComplete={(importSuccessful) => {
									if (importSuccessful) {
										queryClient.invalidateQueries({ queryKey: ['publishers'] });
										showToast({
											message: 'Import successful',
											status: 'success',
											timeout: 3,
										});
									} else {
										showToast({
											message: 'Import failed',
											status: 'error',
										});
									}
								}}
								onError={(error) => {
									showToast({
										message: error,
										status: 'error',
									});
								}}
								onClose={() => setCsvUploadVisible(false)}
							/>
						</div>
					</div>
				)}
			</div>

			{/* Publisher table */}
			<div className="container mx-auto mt-4">
				<SimpleTable
					headers={['Publisher', 'Main Contact', 'Notes']}
					columnClass={['w-[400px] flex-auto', 'w-[400px] flex-auto', 'w-[400px] flex-auto']}
					rows={isLoading ? undefined : rows}
					skeletonRows={5}
					actions={({ id, cols, hidden }) => (
						<div className="flex gap-2 text-[20px]">
							<Tooltip title="View users" disableInteractive>
								<Button icon={<SupervisedUserCircle fontSize="inherit" />} href={`/accounts/${id}/users`} />
							</Tooltip>
							{!hidden ? (
								<ConfirmDialog
									text={<>
										<span>Are you sure you want to hide the "<span className="font-medium">{cols[0]}</span>" publisher?</span>
										<span className="text-sm italic">You may view hidden publishers by toggling "Show hidden" in the menu.</span>
									</>}
									confirmLabel="Hide"
									render={(show) => (
										<Tooltip title="Hide publisher" disableInteractive>
											<Button icon={<VisibilityOff fontSize="inherit" />} onClick={show} />
										</Tooltip>
									)}
									onConfirm={() => { hideMutation.mutate({ id, hidden: true }); }}
								/>
							) : (
								<Tooltip title="Unhide publisher" disableInteractive>
									<Button icon={<Visibility fontSize="inherit" />} onClick={() => { hideMutation.mutate({ id, hidden: false }); }} />
								</Tooltip>
							)}
							<ConfirmDialog
								text={<>
									<span>Are you sure you want to remove the "<span className="font-medium">{cols[0]}</span>" publisher?</span>
									<span>This operation can not be undone.</span>
								</>}
								confirmLabel='Remove'
								render={(show) => (
									<Tooltip title="Remove publisher" disableInteractive>
										<Button icon={<Delete fontSize="inherit" />} onClick={show} disabled={deleteMutation.isPending} />
									</Tooltip>
								)}
								onConfirm={() => deleteMutation.mutate({ id })}
							/>
						</div>
					)}
				/>
			</div>

			{/* Keeping this button in its original spot since it's only relevant to demo mode */}
			<IfDemoMode>
				<div className="container mx-auto mt-4 flex gap-4 items-start">
					<PopupSelector
						onApplyChanges={async ({ preventDefault }) => {
							await demoImport.save(preventDefault);
						}}
						size="lg"
						customLink={(showFn) => (
							<Button variant="outlined" onClick={showFn}>Yield demo setup</Button>
						)}
					>
						<DemoImport ref={(elm) => { demoImport = elm; }} />
					</PopupSelector>
				</div>
			</IfDemoMode>
		</>
	);
}
